import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { differenceInDays, format, parseISO } from 'date-fns';

import { Sparkline } from '@/components/charts';
import { AreasDots, Bullet, Loaded, LOGIN_NEEDED, PageHead, PillTag, Seg, Select } from '@/components/ui';
import { useApi, useMeta } from '@/lib/api';
import { cx } from '@/lib/cx';
import { fmtAgo, fmtInt, fmtPct } from '@/lib/format';
import { useParam } from '@/lib/params';
import type { AccountRow, AccountsList, Segment, UsersList } from '@/lib/types';

const SEGMENTS: { value: Segment; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'fading', label: 'Fading' },
  { value: 'new', label: 'New · 14d' },
  { value: 'high_not_ok', label: 'High not-ok' },
  { value: 'near_budget', label: 'Near AI budget' },
  { value: 'trial_ending', label: 'Trial ending · 7d' },
  { value: 'never_asked', label: 'Never asked AI' },
];

function subLine(r: AccountRow, now: string): string {
  if (r.trial_expires_at && parseISO(r.trial_expires_at) > parseISO(now)) {
    const days = differenceInDays(parseISO(r.trial_expires_at), parseISO(now));
    if (days <= 30) return `trial · ends in ${days} ${days === 1 ? 'day' : 'days'}`;
  }
  return [r.organization, r.created_at && `since ${format(parseISO(r.created_at), 'MMM yyyy')}`, r.top_user]
    .filter(Boolean)
    .join(' · ');
}

function stale(iso: string | null, now: string): boolean {
  return iso !== null && differenceInDays(parseISO(now), parseISO(iso)) >= 7;
}

export function AccountsPage() {
  const meta = useMeta();
  const navigate = useNavigate();
  const [view, setView] = useParam('view', 'companies');
  const [segment, setSegment] = useParam('segment', 'all');
  const [plan, setPlan] = useParam('plan', '');
  const [org, setOrg] = useParam('org', '');
  const [q, setQ] = useParam('q', '');
  const [range] = useParam('range', '30d');
  const [text, setText] = useState(q);
  useEffect(() => setText((t) => (t.trim() === q ? t : q)), [q]);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (text.trim() !== q) setQ(text.trim() || null);
    }, 400);
    return () => clearTimeout(timer);
  }, [text, q, setQ]);
  const users = view === 'users';
  const companies = useApi<AccountsList>('/accounts', { segment, plan, org, q, range }, { enabled: !users });
  const people = useApi<UsersList>('/accounts', { view: 'users', q, range }, { enabled: users });
  const now = meta.data?.now ?? new Date().toISOString();
  const totals = companies.data?.totals;
  const usersCount = people.data ? `${fmtInt(people.data.items.length)}${people.data.truncated ? '+' : ''}` : null;
  return (
    <div className="page">
      <PageHead
        title="Accounts"
        subtitle={
          users
            ? usersCount
              ? `${usersCount} users · last ${range}`
              : ' '
            : totals
              ? `${fmtInt(totals.companies)} customer companies · ${fmtInt(totals.users)} users · last ${range}`
              : ' '
        }
      >
        <div className="search" style={{ width: 260 }}>
          <span className="faint">⌕</span>
          <input value={text} placeholder="Company, user or email…" onChange={(e) => setText(e.target.value)} />
        </div>
        {!users && (
          <>
            <Select
              value={plan}
              active={Boolean(plan)}
              onChange={(v) => setPlan(v || null)}
              options={[
                { value: '', label: 'All plans' },
                ...(meta.data?.plans ?? []).map((p) => ({ value: p, label: p })),
              ]}
            />
            <Select
              value={org}
              active={Boolean(org)}
              onChange={(v) => setOrg(v || null)}
              options={[
                { value: '', label: 'All organisations' },
                ...(companies.data?.organizations ?? []).map((o) => ({ value: String(o.id), label: o.name })),
              ]}
            />
          </>
        )}
        <Seg
          options={[
            { value: 'companies', label: 'Companies' },
            { value: 'users', label: 'Users' },
          ]}
          value={users ? 'users' : 'companies'}
          onChange={setView}
        />
      </PageHead>

      {users ? (
        <Loaded query={people}>
          {(data) => (
            <div className="card" style={{ padding: '6px 8px' }}>
              {data.items.length === 0 ? (
                <div className="muted" style={{ padding: 12 }}>
                  No users match.
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Companies</th>
                      <th className="num">Asks</th>
                      <th className="num">Not-ok</th>
                      <th className="num">Last seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((u) => (
                      <tr key={u.id} onClick={() => navigate(`/users/${u.id}`)}>
                        <td>
                          <b>{u.name ?? u.email}</b>
                          <div className="faint" style={{ fontSize: 11 }}>
                            {u.email}
                          </div>
                        </td>
                        <td>{u.companies.map((c) => c.name).join(', ')}</td>
                        <td className="num">{fmtInt(u.asks)}</td>
                        <td className="num">
                          <span className={cx('pill', (u.not_ok_rate ?? 0) >= 0.1 ? 'bad' : 'neu')}>
                            {fmtPct(u.not_ok_rate)}
                          </span>
                        </td>
                        <td className="num">{fmtAgo(u.last_seen_at, now)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {data.truncated && (
                <div className="note" style={{ padding: '0 8px 8px' }}>
                  Showing the first 300. Narrow the search.
                </div>
              )}
            </div>
          )}
        </Loaded>
      ) : (
        <>
          <div className="chipset" style={{ marginBottom: 12 }}>
            {SEGMENTS.map((s) => (
              <span
                key={s.value}
                className={cx('cchip', segment === s.value && 'on')}
                onClick={() => setSegment(s.value)}
              >
                {s.label}
                <b>{companies.data ? fmtInt(companies.data.segments[s.value]) : ''}</b>
              </span>
            ))}
            <span className="cchip" title={LOGIN_NEEDED} style={{ opacity: 0.5, cursor: 'not-allowed' }}>
              Tagged VIP
            </span>
          </div>
          <Loaded query={companies}>
            {(data) => (
              <div className="card" style={{ padding: '6px 8px' }}>
                {data.items.length === 0 ? (
                  <div className="muted" style={{ padding: 12 }}>
                    No companies match.
                  </div>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>Company</th>
                        <th>Plan</th>
                        <th>Activity · {range}</th>
                        <th className="num">Users</th>
                        <th className="num">Asks</th>
                        <th className="num">Not-ok</th>
                        <th>Areas used</th>
                        <th>AI budget</th>
                        <th className="num">Last seen</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {data.items.map((r) => (
                        <tr key={r.id} onClick={() => navigate(`/accounts/${r.id}`)}>
                          <td>
                            <b>{r.name}</b>
                            <div className="faint" style={{ fontSize: 11 }}>
                              {subLine(r, now)}
                            </div>
                          </td>
                          <td>{r.plan ?? '–'}</td>
                          <td>
                            <Sparkline values={r.spark} width={90} height={22} bad={r.tag?.label === 'Fading'} />
                          </td>
                          <td className="num">
                            {r.users_active}/{r.users_total}
                          </td>
                          <td className="num">{fmtInt(r.asks)}</td>
                          <td className="num">
                            <span className={cx('pill', (r.not_ok_rate ?? 0) >= 0.1 ? 'bad' : 'neu')}>
                              {fmtPct(r.not_ok_rate)}
                            </span>
                          </td>
                          <td>
                            <AreasDots used={r.areas} all={data.used_areas} />
                          </td>
                          <td>
                            {r.budget ? (
                              <Bullet share={r.budget.share} pace={r.budget.pace} />
                            ) : (
                              <span className="faint">–</span>
                            )}
                          </td>
                          <td
                            className="num"
                            style={stale(r.last_seen_at, now) ? { color: 'var(--warn)', fontWeight: 700 } : undefined}
                          >
                            {fmtAgo(r.last_seen_at, now)}
                          </td>
                          <td>{r.tag && <PillTag pill={r.tag} />}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {data.truncated && (
                  <div className="note" style={{ padding: '0 8px 8px' }}>
                    Showing the first 300. Narrow the search or pick a segment.
                  </div>
                )}
                <div className="note" style={{ padding: '0 8px 8px' }}>
                  Areas used:{' '}
                  {(meta.data?.areas ?? [])
                    .slice(0, 9)
                    .map((a) => a.short)
                    .join(', ')}
                  . AI budget bar: share of this month's budget used; the tick marks how far into the month we are.
                </div>
              </div>
            )}
          </Loaded>
        </>
      )}
    </div>
  );
}
